import { useState, useCallback } from 'react';
import { router, Link } from '@inertiajs/react';
import GmLayout from '@/layouts/GmLayout';
import { GmItemsIndexProps, ItemRecipe, ItemRarity, PaginationLink } from '@/types/items';

/**
 * GM Items Index page - displays all item recipes with management actions.
 */
export default function Index({ recipes, rarities, filters }: GmItemsIndexProps) {
    const [search, setSearch] = useState(filters.search || '');
    const [rarityFilter, setRarityFilter] = useState(filters.rarity || '');

    // Handle search/filter submission
    const handleFilter = useCallback(() => {
        router.get('/gm/items', {
            search: search || undefined,
            rarity: rarityFilter || undefined,
        }, { preserveState: true });
    }, [search, rarityFilter]);

    // Clear all filters
    const handleClearFilters = () => {
        setSearch('');
        setRarityFilter('');
        router.get('/gm/items', {}, { preserveState: true });
    };

    // Handle delete
    const handleDelete = (recipe: ItemRecipe) => {
        if (confirm(`Are you sure you want to delete "${recipe.name}"? This will also delete all ${recipe.items_count} item instances.`)) {
            router.delete(`/gm/items/${recipe.id}`, { preserveScroll: true });
        }
    };

    return (
        <GmLayout title="Item Recipes" activePath="/gm/items">
            <div className="max-w-7xl">
                <div className="flex items-center justify-between mb-6">
                    <h1 className="text-2xl font-bold text-white">Item Recipes</h1>
                    <div className="flex items-center gap-4">
                        <Link
                            href="/gm/items/awarded"
                            className="px-4 py-2 bg-slate-600 hover:bg-slate-500 text-white rounded transition-colors"
                        >
                            View Awarded Items
                        </Link>
                        <Link
                            href="/gm/items/create"
                            className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded transition-colors"
                        >
                            + Create Recipe
                        </Link>
                    </div>
                </div>

                {/* Search and Filters */}
                <div className="bg-slate-800 border border-slate-700 rounded-lg p-4 mb-6">
                    <div className="flex flex-wrap gap-4 items-end">
                        {/* Search Input */}
                        <div className="flex-1 min-w-[200px]">
                            <label className="block text-sm text-gray-400 mb-1">Search</label>
                            <input
                                type="text"
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                onKeyDown={(e) => e.key === 'Enter' && handleFilter()}
                                placeholder="Item name or description"
                                className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded text-white placeholder-gray-400"
                            />
                        </div>

                        {/* Rarity Filter */}
                        <div className="w-40">
                            <label className="block text-sm text-gray-400 mb-1">Rarity</label>
                            <select
                                value={rarityFilter}
                                onChange={(e) => setRarityFilter(e.target.value)}
                                className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded text-white"
                            >
                                <option value="">All Rarities</option>
                                {rarities.map((rarity) => (
                                    <option key={rarity.value} value={rarity.value}>
                                        {rarity.label}
                                    </option>
                                ))}
                            </select>
                        </div>

                        {/* Filter Buttons */}
                        <div className="flex gap-2">
                            <button
                                onClick={handleFilter}
                                className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded transition-colors"
                            >
                                Apply
                            </button>
                            <button
                                onClick={handleClearFilters}
                                className="px-4 py-2 bg-slate-600 hover:bg-slate-500 text-white rounded transition-colors"
                            >
                                Clear
                            </button>
                        </div>
                    </div>
                </div>

                {/* Recipes Table */}
                {recipes.data.length === 0 ? (
                    <div className="text-center py-12 text-gray-400">
                        No item recipes found.
                    </div>
                ) : (
                    <div className="bg-slate-800 border border-slate-700 rounded-lg overflow-hidden">
                        <div className="overflow-x-auto">
                            <table className="w-full">
                                <thead className="bg-slate-700">
                                    <tr>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Name</th>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Rarity</th>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Stats</th>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Awarded</th>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Actions</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-700">
                                    {recipes.data.map((recipe) => (
                                        <RecipeRow key={recipe.id} recipe={recipe} onDelete={handleDelete} />
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}

                {/* Pagination */}
                {recipes.last_page > 1 && (
                    <Pagination links={recipes.links} />
                )}
            </div>
        </GmLayout>
    );
}

/**
 * RecipeRow - Displays a single item recipe row with actions.
 */
function RecipeRow({ recipe, onDelete }: { recipe: ItemRecipe; onDelete: (recipe: ItemRecipe) => void }) {
    return (
        <tr>
            {/* Name */}
            <td className="px-4 py-3">
                <div>
                    <p className="text-white font-medium">{recipe.name}</p>
                    {recipe.description && (
                        <p className="text-xs text-gray-400 truncate max-w-xs">{recipe.description}</p>
                    )}
                </div>
            </td>

            {/* Rarity */}
            <td className="px-4 py-3">
                <span className={recipe.rarity.tailwindClass}>
                    {recipe.rarity.label}
                </span>
            </td>

            {/* Stats */}
            <td className="px-4 py-3 text-sm text-gray-300">
                {recipe.stat_count} stat{recipe.stat_count !== 1 ? 's' : ''} ({recipe.base_stat_range[0]}-{recipe.base_stat_range[1]})
            </td>

            {/* Awarded Count */}
            <td className="px-4 py-3 text-sm text-gray-400">
                {recipe.items_count} item{recipe.items_count !== 1 ? 's' : ''}
            </td>

            {/* Actions */}
            <td className="px-4 py-3">
                <div className="flex gap-2">
                    <Link
                        href={`/gm/items/award?recipe_id=${recipe.id}`}
                        className="px-3 py-1 text-xs bg-green-600 hover:bg-green-700 text-white rounded transition-colors"
                    >
                        Award
                    </Link>
                    <Link
                        href={`/gm/items/${recipe.id}/copy`}
                        className="px-3 py-1 text-xs bg-purple-600 hover:bg-purple-700 text-white rounded transition-colors"
                        title="Create a copy of this recipe"
                    >
                        Copy
                    </Link>
                    <Link
                        href={`/gm/items/${recipe.id}/edit`}
                        className="px-3 py-1 text-xs bg-blue-600 hover:bg-blue-700 text-white rounded transition-colors"
                    >
                        Edit
                    </Link>
                    <button
                        onClick={() => onDelete(recipe)}
                        className="px-3 py-1 text-xs bg-red-600 hover:bg-red-700 text-white rounded transition-colors"
                    >
                        Delete
                    </button>
                </div>
            </td>
        </tr>
    );
}

/**
 * Pagination - Displays pagination links.
 */
function Pagination({ links }: { links: PaginationLink[] }) {
    return (
        <div className="mt-6 flex justify-center gap-1">
            {links.map((link, index) => (
                <a
                    key={index}
                    href={link.url || '#'}
                    className={`px-3 py-1 rounded text-sm ${
                        link.active
                            ? 'bg-purple-600 text-white'
                            : link.url
                            ? 'bg-slate-700 text-gray-300 hover:bg-slate-600'
                            : 'bg-slate-800 text-gray-500 cursor-not-allowed'
                    }`}
                    dangerouslySetInnerHTML={{ __html: link.label }}
                />
            ))}
        </div>
    );
}

